import { getCookie, removeCookie, setCookie } from '@visa/lib-cookie';
import {
  ALWAYS_REMEMBER_COOKIE,
  ERM_ENABLED,
  SSI_DECLINED,
  SSI_ENABLED
} from '../../constants/cookie';
import isEmptyString from '../../helpers/is-empty-string';
import { getUsername } from '../../helpers/rememberme-cookie-helper';

const ONE_YEAR_IN_MS = 52 * 7 * 24 * 60 * 60 * 1000;
const SIX_MONTHS_IN_MS = ONE_YEAR_IN_MS / 2;
const TWO_MONTHS_IN_MS = SIX_MONTHS_IN_MS / 3;

/**
 * Get ssi/erm cookie timestamp expiry
 * @param {number} EXPIRY_TIMESTAMP
 * @returns {string} timestamp calculated from today's date + EXPIRY_TIMESTAMP
 */
const getCookieExpireTimestamp = function(EXPIRY_TIMESTAMP) {
  return String(Number(new Date()) + EXPIRY_TIMESTAMP);
};

/**
 * Check if alwaysRemember cookie present
 * @returns {boolean} true if alwaysRemember cookie present
 */
export const alwaysRememberIsTrue = function() {
  try {
    return Boolean(JSON.parse(getCookie(ALWAYS_REMEMBER_COOKIE)));
  } catch (error) {
    // Boolean cast failed; cookie val was not present, or not "true" or "false"
  }
  return false;
};

/**
 * Get decoded cookie values
 * @param {string} cookieName
 * @returns {object} cookie value in object
 */
export const decodeCookieValues = function(cookieName) {
  let ret;
  try {
    const decodedCookieValue = decodeURIComponent(getCookie(cookieName));
    ret = decodedCookieValue && JSON.parse(decodedCookieValue);
    if (ret && ret.date) {
      ret.date = parseInt(ret.date, 10);
    }
  } catch (e) {
    /* malformed data */
  }
  return ret;
};

/**
 * Get ssiEnabled parsed/decoded cookie value
 * @returns {object} cookie value
 */
export const getSsiEnabledCookieValues = function() {
  return decodeCookieValues(SSI_ENABLED);
};

/**
 * Get ssiEnabled parsed/decoded cookie value
 * @returns {object} cookie value
 */
export const getErmEnabledCookieValues = function() {
  return decodeCookieValues(ERM_ENABLED);
};

/**
 * Get ssiDeclined cookie value
 * @returns {string} cookie value
 */
export const getSsiDeclinedCookieValues = function() {
  return getCookie(SSI_DECLINED);
};

/**
 * Check if username cookie is present
 * @returns {boolean} true, if username cookie present
 */
export const isUserRecognized = function() {
  return Boolean(getUsername());
};

/**
 * Set ssiDecliened cookie
 *  - By default set cookie expiry to today's date + 2 months timestamp
 */
export const setEnrollOptOutStorage = function() {
  //removeEnrollOptInStorage();
  setCookie(SSI_DECLINED, getCookieExpireTimestamp(TWO_MONTHS_IN_MS));
};

/**
 * Set ssiEnabled cookie
 *  - By default set cookie expiry to today's date + 6 months timestamp
 */
export const setSSIEnabled = function() {
  const date = getCookieExpireTimestamp(SIX_MONTHS_IN_MS);
  //F11669 get the username from the remember_me cookie
  const username = getUsername();
  setCookie(
    SSI_ENABLED,
    JSON.stringify({
      date,
      username
    }),
    180
  );
};

/**
 * Set ermEnabled cookie
 *  - By default set cookie expiry to today's date + 6 months timestamp
 *  - PO requirement is to set the cookie expiry to 6 months.
 * @param {string} authRef
 */
export const setErmEnabled = function(authRef) {
  const date = getCookieExpireTimestamp(SIX_MONTHS_IN_MS);
  //F11669 get the username from the remember_me cookie
  const username = getUsername();
  setCookie(
    ERM_ENABLED,
    JSON.stringify({
      authRef,
      date,
      username
    }),
    180
  );
};

/**
 * Delete ssiEnabled, ssiDecliened & ermEnabled cookies
 * - Invoked by isRecognized/signout functionality as part of sign-in or username cookie change.
 */
export const deleteSsiErmCookies = function() {
  const ssiErmCookies = [ERM_ENABLED, SSI_ENABLED, SSI_DECLINED];
  for (const cookie of ssiErmCookies) {
    removeCookie(cookie);
  }
};

/**
 * Reset SSI with username from remember_me cookie
 */
export const updateUsernameInSsiCookie = function() {
  //TODO Queries related to the cookie expiry. Following setSsiEnabled will reset the expiry for next 180 days.
  const isSsiEnabled = Boolean(getCookie(SSI_ENABLED));
  if (isSsiEnabled) {
    setSSIEnabled();
  }
};

/**
 * Reset ERM with username from remember_me cookie
 */
export const updateUsernameInErmCookie = function() {
  //TODO Queries related to the cookie expiry. Following setErmEnabled will reset the expiry for next 180 days.
  const ermEnabled = getErmEnabledCookieValues();
  const authRef = (ermEnabled && ermEnabled.authRef) || null;
  if (authRef) {
    setErmEnabled(authRef);
  }
};

/**
 *  Set username from rememberMe cookies
 *  - delete the existing username cookie if remember_me is present and reset
 *  - reset/update username in ssi and erm cookies.
 * @param {boolean} resetErmCookie option to reset ERM username
 * @returns {string} emailFromRememberMeCookie
 */
export const setUsernameFromRememberMeCookie = function(resetErmCookie) {
  const emailFromRememberMeCookie = getUsername();
  if (!isEmptyString(emailFromRememberMeCookie)) {
    updateUsernameInSsiCookie();
    if (resetErmCookie) {
      updateUsernameInErmCookie();
    }
  }

  return emailFromRememberMeCookie;
};
